import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Radio,
  FormControlLabel,
  Checkbox,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as services from "./../../../services/saleng";
import moment from "moment";
import "moment/locale/th";

export default function POSelectionDialog({
  open,
  onClose,
  transaction,
  poSelect,
  setPoSelect,
  lockSelect,
  setLockSelect,
  lockItems,
  poItems,
}) {
  const [selectedRadio, setSelectedRadio] = useState("none"); // "none" | "po" | "lock"
  const [checkedItems, setCheckedItems] = useState([]); // ids ของ PO
  const [lockCheckedItems, setLockCheckedItems] = useState([]); // ids ของ lock
  const [lockStartDate, setLockStartDate] = useState("");
  const [lockEndDate, setLockEndDate] = useState("");

  const maxSelect = 10;

  // ตั้ง default วันที่ช่วง lock: start = min(createdAt), end = today
  useEffect(() => {
    if (lockItems.length === 0) return;

    const itemsWithCreated = lockItems.filter((x) => !!x?.createdAt);
    if (itemsWithCreated.length === 0) return;

    const minCreatedAt = itemsWithCreated.reduce((min, item) => {
      const d = new Date(item.createdAt);
      return d < min ? d : min;
    }, new Date(itemsWithCreated[0].createdAt));

    const startStr = minCreatedAt.toISOString().split("T")[0];
    const endStr = new Date().toISOString().split("T")[0];

    setLockStartDate((prev) => (prev ? prev : startStr));
    setLockEndDate((prev) => (prev ? prev : endStr));
  }, [lockItems]);

  // sync radio กับ poSelect (มีค่า → โหมด "po", ว่าง → "none")
  useEffect(() => {
    if (!open) return;

    if (poSelect && poSelect.length > 0) {
      const ids = (poSelect ?? []).map((po) => po.purchaseOrderId);
      setCheckedItems(ids);
      setSelectedRadio("po");
    } else if (lockSelect && lockSelect.length > 0) {
      const ids = lockSelect.map((l) => l.id);
      setLockCheckedItems(ids);
      setSelectedRadio("lock");
    } else {
      setCheckedItems([]);
      setSelectedRadio("none");
    }
  }, [lockSelect, open, poSelect]);

  // Auto select PO ตาม poSelect เมื่อโหลดรายการ PO เสร็จ
  useEffect(() => {
    if (!open || poItems.length === 0) return;

    const autoCheckedIds = poItems
      .filter((po) => poSelect.some((s) => s.id === po.id))
      .map((po) => po.id);

    if (autoCheckedIds.length > 0) {
      setCheckedItems(autoCheckedIds);
      setSelectedRadio("po");
    }
  }, [open, poItems, poSelect]);

  const handleToggleCheck = (id, type) => {
    if (type === "po") {
      setCheckedItems((prev) => {
        if (prev.includes(id)) return prev.filter((v) => v !== id);
        if (prev.length < maxSelect) return [...prev, id];
        return prev;
      });
    } else if (type === "lock") {
      setLockCheckedItems((prev) => {
        if (prev.includes(id)) return prev.filter((v) => v !== id);
        return [...prev, id]; // lock ไม่มี limit
      });
    }
  };

  const handleSelectAll = (type, rows) => {
    const ids = rows.map((r) => Number(r.id));
    if (type === "po") setCheckedItems(ids);
    else setLockCheckedItems(ids);
  };

  // PO columns/rows
  const poColumns = [
    {
      field: "checkbox",
      headerName: "",
      width: 50,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Checkbox
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          checked={checkedItems.length === poRows.length && poRows.length > 0}
          indeterminate={
            checkedItems.length > 0 && checkedItems.length < poRows.length
          }
          onChange={(e) =>
            e.target.checked
              ? handleSelectAll("po", poRows)
              : setCheckedItems([])
          }
        />
      ),
      renderCell: (params) => (
        <Checkbox
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          checked={checkedItems.includes(params.row.id)}
          onChange={() => handleToggleCheck(params.row.id, "po")}
        />
      ),
    },
    { field: "orderNumber", headerName: "PO #", width: 100 },
    { field: "detail", headerName: "รายละเอียด", width: 300 },
    { field: "quantity", headerName: "จำนวนสินค้า", width: 130 },
    { field: "startDate", headerName: "เริ่มต้น", width: 120 },
    { field: "endDate", headerName: "สิ้นสุด", width: 120 },
  ];

  const poRows = poItems.map((t) => ({
    id: t.id,
    orderNumber: t.id?.toString().padStart(4, "0"),
    detail: t.detail || "-",
    quantity: `${t.items?.length ?? 0} ไอเท็ม`,
    startDate: t.startDate
      ? moment(t.startDate).add(543, "year").format("D MMM YYYY")
      : "-",
    endDate: t.endDate
      ? moment(t.endDate).add(543, "year").format("D MMM YYYY")
      : "-",
  }));

  // LOCK columns/rows
  const lockColumns = [
    {
      field: "checkbox",
      headerName: "",
      width: 50,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Checkbox
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          checked={
            lockCheckedItems.length === filteredLockRows.length &&
            filteredLockRows.length > 0
          }
          indeterminate={
            lockCheckedItems.length > 0 &&
            lockCheckedItems.length < filteredLockRows.length
          }
          onChange={(e) =>
            e.target.checked
              ? handleSelectAll("lock", filteredLockRows)
              : setLockCheckedItems([])
          }
        />
      ),
      renderCell: (params) => (
        <Checkbox
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          checked={lockCheckedItems.includes(params.row.id)}
          onChange={() => handleToggleCheck(params.row.id, "lock")}
        />
      ),
    },
    { field: "productName", headerName: "สินค้า", width: 300 },
    { field: "lockWeight", headerName: "น้ำหนักล็อค", width: 120 },
    { field: "actualWeight", headerName: "น้ำหนักจริง", width: 120 },
    { field: "purchasePrice", headerName: "ราคา/หน่วย", width: 120 },
    { field: "predictPrice", headerName: "ราคาทำนาย", width: 120 },
    { field: "status", headerName: "สถานะ", width: 100 },
  ];

  const filteredLockRows = lockItems
    .filter((t) => {
      if (!lockStartDate || !lockEndDate) return false;
      // เงื่อนไขช่วงวันที่ของรายการ lock (ถ้ามี start/end ใน item)
      const startOk = t.startDate
        ? new Date(t.startDate) >= new Date(lockStartDate)
        : true;
      const endOk = t.endDate
        ? new Date(t.endDate) <= new Date(lockEndDate)
        : true;
      return startOk && endOk;
    })
    .map((t) => ({
      id: t.id,
      productName: t.transactionItem?.productName || t.product?.title || "-",
      lockWeight: t.lockWeight ?? 0,
      actualWeight: t.actualWeight ?? 0,
      purchasePrice: t.purchasePrice ?? 0,
      predictPrice: t.predictPrice ?? 0,
      status: t.status || "-",
    }));

  return (
    <Dialog open={open} onClose={() => onClose()} fullWidth maxWidth="lg">
      <DialogTitle>การล็อคราคาสินค้า</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          กรุณาเลือกประเภทการล็อคราคาสินค้าถ้าต้องการใช้กับBatchนี้
        </Typography>

        {/* PO */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            value="po"
            control={
              <Radio
                checked={selectedRadio === "po"}
                onChange={() => {
                  setCheckedItems([]);
                  setLockCheckedItems([]);
                  setSelectedRadio("po");
                }}
              />
            }
            label={
              <Typography>
                ใบราคาสินค้า (PO) — {checkedItems.length}/{maxSelect}
              </Typography>
            }
          />
          {selectedRadio === "po" && (
            <Box
              sx={{
                border: "1px solid #ddd",
                borderRadius: 1,
                p: 1,
                mt: 1,
                backgroundColor: "#fafafa",
              }}
            >
              <Typography sx={{ mb: 1 }}>
                จำนวนรายการ: {poRows.length}
              </Typography>
              <DataGrid
                rows={poRows}
                columns={poColumns}
                autoHeight
                disableRowSelectionOnClick
                hideFooter
                sx={{ "& .MuiDataGrid-columnHeaders": { fontWeight: "bold" } }}
              />
            </Box>
          )}
        </Box>

        {/* Lock */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            value="lock"
            control={
              <Radio
                checked={selectedRadio === "lock"}
                onChange={() => {
                  setCheckedItems([]);
                  setLockCheckedItems([]);
                  setSelectedRadio("lock");
                }}
              />
            }
            label={`สินค้าล็อคราคา — ${lockCheckedItems.length} เลือกแล้ว`}
          />
          {selectedRadio === "lock" && (
            <Box sx={{ mt: 1 }}>
              <Box display="flex" gap={2} mb={1} alignItems="center">
                <TextField
                  type="date"
                  label="เริ่มต้น"
                  value={lockStartDate}
                  onChange={(e) => setLockStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Typography>—</Typography>
                <TextField
                  type="date"
                  label="สิ้นสุด"
                  value={lockEndDate}
                  onChange={(e) => setLockEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              {lockStartDate && lockEndDate && (
                <Box
                  sx={{
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    p: 1,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Typography sx={{ mb: 1 }}>
                    จำนวนรายการ: {filteredLockRows.length}
                  </Typography>
                  <DataGrid
                    rows={filteredLockRows}
                    columns={lockColumns}
                    autoHeight
                    disableRowSelectionOnClick
                    hideFooter
                    sx={{
                      "& .MuiDataGrid-columnHeaders": { fontWeight: "bold" },
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* None */}
        <Box>
          <FormControlLabel
            value="none"
            control={
              <Radio
                checked={selectedRadio === "none"}
                onChange={() => {
                  setCheckedItems([]);
                  setLockCheckedItems([]);
                  setSelectedRadio("none");
                }}
              />
            }
            label="ไม่ใช้การล็อคราคา (ทั่วไป)"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 0 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            if (selectedRadio === "po") {
              const selectedPOs = poItems.filter((po) =>
                checkedItems.includes(po.id)
              );
              setPoSelect(selectedPOs);
              setLockSelect([]);
              onClose();
              return;
            } else if (selectedRadio === "lock") {
              console.log("lock", lockCheckedItems);
              const selectedLocks = lockItems.filter((lk) =>
                lockCheckedItems.includes(lk.id)
              );
              console.log("selectedLocks", selectedLocks);
              setPoSelect([]);
              setLockSelect(selectedLocks);
              onClose();
              return;
            } else if (selectedRadio === "none") {
              setPoSelect([]);
              setLockSelect([]);
              onClose();
              return;
            }
          }}
          sx={{
            borderRadius: 0,
            py: 1.8,
            fontWeight: 700,
            bgcolor: "#1E2630",
            "&:hover": { bgcolor: "#111820" },
          }}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </Dialog>
  );
}
